import React, { useContext, useState } from 'react';
import { AdminContext } from '../AdminContext';

export default function Export() {
  const { token } = useContext(AdminContext);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTables, setSelectedTables] = useState({
    hackathon: true,
    volunteers: false,
    ceremony: false
  });

  const handleExport = async (type = 'hackathon') => {
    setDownloading(true);
    setError('');
    try {
      let url = '';
      let filename = '';
      
      if (type === 'hackathon') {
        url = '/.netlify/functions/admin-export-registrations';
        filename = 'hackathon_registrations.csv';
      } else if (type === 'selective') {
        const tables = Object.entries(selectedTables)
          .filter(([key, value]) => value)
          .map(([key]) => key)
          .join(',');
        
        if (!tables) {
          throw new Error('Please select at least one data type to export.');
        }
        
        url = `/.netlify/functions/admin-export-selective?tables=${tables}`;
        filename = `hackathon_export_${new Date().toISOString().split('T')[0]}.csv`;
      }
      
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Check if response is not ok before trying to parse
      if (!res.ok) {
        const text = await res.text();
        if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
          throw new Error('Server returned HTML instead of JSON. Please check if the backend is running correctly.');
        }
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      // Get the response as blob (CSV file)
      const blob = await res.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
    } catch (err) {
      console.error('Error exporting data:', err);
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleTableChange = (tableName) => {
    setSelectedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedTables).every(v => v);
    setSelectedTables({
      hackathon: !allSelected,
      volunteers: !allSelected,
      ceremony: !allSelected
    });
  };

  const selectedCount = Object.values(selectedTables).filter(v => v).length;

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: 24 }}>
      <h2>Export Registrations</h2>
      
      {/* Quick Export Buttons */}
      <div style={{ marginBottom: 30 }}>
        <h3>Quick Export</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => handleExport('hackathon')}
            disabled={downloading}
            style={{
              padding: '12px 24px',
              background: '#006400',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: '16px',
              cursor: downloading ? 'not-allowed' : 'pointer'
            }}
          >
            {downloading ? 'Exporting...' : 'Export Hackathon Only'}
          </button>
          
          <button
            onClick={() => handleExport('selective')}
            disabled={downloading || selectedCount === 0}
            style={{
              padding: '12px 24px',
              background: selectedCount > 0 ? '#006400' : '#666',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: '16px',
              cursor: (downloading || selectedCount === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            {downloading ? 'Exporting...' : `Export Selected (${selectedCount})`}
          </button>
        </div>
      </div>

      {/* Selective Export Section */}
      <div style={{ marginBottom: 30 }}>
        <h3>Selective Export</h3>
        <p>Choose which data types to include in your export:</p>
        
        <div style={{ margin: '20px 0' }}>
          <button
            onClick={handleSelectAll}
            style={{
              padding: '8px 16px',
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: 4,
              marginBottom: 15,
              cursor: 'pointer'
            }}
          >
            {Object.values(selectedTables).every(v => v) ? 'Deselect All' : 'Select All'}
          </button>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedTables.hackathon}
                onChange={() => handleTableChange('hackathon')}
                style={{ marginRight: 8, transform: 'scale(1.2)' }}
              />
              <span><strong>Hackathon Registrations</strong> - Main hackathon participants with skills, motivation, and experience</span>
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedTables.volunteers}
                onChange={() => handleTableChange('volunteers')}
                style={{ marginRight: 8, transform: 'scale(1.2)' }}
              />
              <span><strong>Volunteers</strong> - Event volunteers with categories, interests, and contact information</span>
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedTables.ceremony}
                onChange={() => handleTableChange('ceremony')}
                style={{ marginRight: 8, transform: 'scale(1.2)' }}
              />
              <span><strong>Ceremony Registrations</strong> - Closing ceremony attendees with organization and interest details</span>
            </label>
          </div>
        </div>
      </div>

      {/* Export Format Info */}
      <div style={{ background: '#f9f9f9', padding: 15, borderRadius: 6, marginBottom: 20 }}>
        <h4>Export Format</h4>
        <p>All exports are in CSV format with the following structure:</p>
        <ul style={{ marginLeft: 20 }}>
          <li>Each data type is separated by headers</li>
          <li>Fields with special characters are properly quoted</li>
          <li>Dates are exported in ISO format</li>
          <li>Empty fields are displayed as empty strings</li>
        </ul>
      </div>

      {error && <div style={{ color: 'red', marginTop: 12, padding: 10, background: '#ffebee', borderRadius: 4 }}>{error}</div>}
    </div>
  );
}
